"use client";

import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { MacroLineFigure } from "@/components/tools/econ-macro/line-figure";
import type { Scale, Strings } from "@/components/tools/real-gdp-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { Known, PeriodResult } from "@/lib/tool-kit/econ-macro/gdp";
import { moneyOptions } from "@/lib/tool-kit/finance/currency";

const clean = (value: number) => (value === 0 ? 0 : value);

function suffixOf(scale: Scale, strings: Strings): string {
  const words: Record<Scale, string> = {
    units: "",
    thousand: strings.suffixThousand,
    million: strings.suffixMillion,
    billion: strings.suffixBillion,
    trillion: strings.suffixTrillion,
  };
  return words[scale];
}

function WorkingLines({
  known,
  latest,
  before,
  strings,
  num,
  pct,
}: {
  known: Known;
  latest: PeriodResult;
  before: PeriodResult | undefined;
  strings: Strings;
  num: (value: number) => string;
  pct: (value: number | null) => string;
}) {
  const values = {
    nominal: num(latest.nominal),
    real: num(latest.real),
    deflator: num(latest.deflator),
  };
  const first = {
    "nominal-deflator": strings.workReal,
    "real-deflator": strings.workNominal,
    "nominal-real": strings.workDeflator,
  }[known];
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>
        {t(strings.workingHeading, { label: latest.label })}
      </h3>
      <p className={line}>{t(first, values)}</p>
      {before && latest.realGrowth !== null && latest.inflation !== null ? (
        <>
          <p className={line}>
            {t(strings.workRealGrowth, {
              real: num(latest.real),
              realBefore: num(before.real),
              growth: pct(latest.realGrowth),
            })}
          </p>
          <p className={line}>
            {t(strings.workInflation, {
              deflator: num(latest.deflator),
              deflatorBefore: num(before.deflator),
              inflation: pct(latest.inflation),
            })}
          </p>
          <p className={line}>
            {t(strings.workIdentity, {
              realFactor: num(1 + latest.realGrowth),
              inflationFactor: num(1 + latest.inflation),
              nominalFactor: num(1 + (latest.nominalGrowth ?? 0)),
            })}
          </p>
        </>
      ) : null}
    </div>
  );
}

export function GdpResults({
  results,
  known,
  currency,
  scale,
  strings,
}: {
  results: PeriodResult[];
  known: Known;
  currency: string;
  scale: Scale;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = {
    ...moneyOptions(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  };
  const suffix = suffixOf(scale, strings);
  const amount = (value: number) => {
    const text = fmt(value, money);
    return suffix
      ? t(strings.amountWithScale, { amount: text, scale: suffix })
      : text;
  };
  const num = (value: number) =>
    fmt(clean(value), { maximumFractionDigits: 4 });
  const pct = (value: number | null) =>
    value === null
      ? strings.notAvailable
      : `${fmt(clean(value) * 100, { maximumFractionDigits: 2 })}%`;
  const latest = results.at(-1);
  if (!latest) {
    return null;
  }
  const before = results.length > 1 ? results.at(-2) : undefined;
  const hasPerCapita = results.some((row) => row.realPerCapita !== null);
  const perPerson = (value: number | null) =>
    value === null ? strings.notAvailable : fmt(value, money);
  return (
    <div className="space-y-5">
      {before ? (
        <>
          <h3 className={WIDGET_H3}>
            {t(strings.latestHeading, { label: latest.label })}
          </h3>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.realGrowthCard}
              value={pct(latest.realGrowth)}
            />
            <StatCard
              label={strings.inflationCard}
              value={pct(latest.inflation)}
            />
            <StatCard
              label={strings.nominalGrowthCard}
              value={pct(latest.nominalGrowth)}
            />
          </StatGrid>
        </>
      ) : null}
      {latest.realPerCapita !== null && !before ? (
        <StatGrid>
          <StatCard
            label={strings.realPerCapitaCard}
            value={perPerson(latest.realPerCapita)}
          />
        </StatGrid>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.periodColumn,
          strings.nominalColumn,
          strings.realColumn,
          strings.deflatorColumn,
          ...(hasPerCapita ? [strings.perCapitaColumn] : []),
        ]}
        rows={results.map((row) => [
          row.label,
          amount(row.nominal),
          amount(row.real),
          num(row.deflator),
          ...(hasPerCapita ? [perPerson(row.realPerCapita)] : []),
        ])}
      />
      {before ? (
        <>
          <h3 className={WIDGET_H3}>{strings.growthHeading}</h3>
          <DataTable
            head={[
              strings.periodColumn,
              strings.nominalGrowthColumn,
              strings.realGrowthColumn,
              strings.inflationColumn,
              ...(hasPerCapita ? [strings.perCapitaGrowthColumn] : []),
            ]}
            rows={results.map((row) => [
              row.label,
              pct(row.nominalGrowth),
              pct(row.realGrowth),
              pct(row.inflation),
              ...(hasPerCapita ? [pct(row.perCapitaGrowth)] : []),
            ])}
          />
        </>
      ) : null}
      <WorkingLines
        before={before}
        known={known}
        latest={latest}
        num={num}
        pct={pct}
        strings={strings}
      />
      {results.length > 1 ? (
        <MacroLineFigure
          description={t(strings.chartDescription, {
            count: fmt(results.length),
            real: amount(latest.real),
            nominal: amount(latest.nominal),
          })}
          fallbackName="real-gdp"
          fmt={fmt}
          id="gdp-chart"
          labels={strings}
          names={[strings.seriesNominal, strings.seriesReal]}
          numeric={false}
          rows={results.map((row) => ({
            label: row.label,
            x: null,
            values: [row.nominal, row.real],
          }))}
          title={strings.chartTitle}
          xTitle={strings.chartXTitle}
          yTitle={strings.chartYTitle}
        />
      ) : null}
    </div>
  );
}
